import { Box } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';

import { ModalServiceContext, RicosContext } from './../../../ricos-context';
import type { Link } from './../../../ricos-schema/next-schema';
import { useBiNext } from './../../../_shared/bi-context-provider/bi-context-provider';
import { useNodeAttrs } from './../../../_shared/use-node-attrs';
import { unreachable } from './../../../editor-common';
import { Panel, PanelHeader } from './../../../ui-components';

import type { SettingsType } from '../../consts';
import { GALLERY_SETTINGS_MODAL_ID } from '../../consts';
import { SETTINGS_MODAL } from '../../data-hooks';
import type { GalleryItem, SelectFileFn } from './common-types';
import classes from './gallery-settings-modal-scss';
import { GeneralSettings } from './general-settings/general-settings';
import { LayoutSettings } from './layout-settings/layout-settings';
import { MediaList } from './media-settings/media-list/media-list';
import { MediaSettingsForm } from './media-settings/media-settings-form';
import { TabNavigation } from './tab-navigation';

export type GallerySettingsModalProps = {
  nodeId: string;
  modalId: string;
  onFileSelect: SelectFileFn;
  accept: string;
  activeTab: SettingsType;
  _state?: GallerySettingsModalState; // USED FOR TESTING PURPOSES ONLY
};

type GallerySettingsModalState =
  | {
      tab: 'design';
      isImageSettingsOpened: boolean;
      selectedItemIndexes: number[];
    }
  | {
      tab: 'settings' | 'layout';
    };

export const GallerySettingsModal: FC<GallerySettingsModalProps> = ({
  nodeId,
  modalId,
  onFileSelect,
  activeTab: initialActiveTab,
  accept,
  _state,
}) => {
  const modalService = useContext(ModalServiceContext) || {};
  const { t, isMobile } = useContext(RicosContext);
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, 'GALLERY');
  const logBi = useBiNext({ pluginId: 'gallery', modalId: GALLERY_SETTINGS_MODAL_ID, nodeId });

  const [state, setState] = useState<GallerySettingsModalState>((): GallerySettingsModalState => {
    if (_state) {
      return _state;
    }

    if (initialActiveTab === 'design') {
      return {
        tab: 'design',
        isImageSettingsOpened: false,
        selectedItemIndexes: [],
      };
    }
    return {
      tab: initialActiveTab,
    };
  });

  if (!pmNodeAttrs || !pmNodeAttrs.items) {
    return null;
  }

  const galleryItems: GalleryItem[] = pmNodeAttrs.items.map((item, index) => ({
    ...item,
    selected: state.tab === 'design' && state.selectedItemIndexes.includes(index),
    id: index.toString(),
  }));

  const isImageSettingsOpened = state.tab === 'design' && state.isImageSettingsOpened;

  const closeModal = () => modalService.closeModal(modalId);

  return (
    <div className={classes.modal}>
      <Panel
        onCloseButtonClick={closeModal}
        dataHook={SETTINGS_MODAL}
        maxHeight={isMobile ? undefined : 630}
        onBackButtonClick={
          isImageSettingsOpened
            ? () => {
                setState(prev => {
                  return {
                    ...prev,
                    selectedItemIndexes: [],
                    isImageSettingsOpened: false,
                  };
                });
              }
            : undefined
        }
      >
        <PanelHeader
          title={t(
            isImageSettingsOpened
              ? 'GallerySettings_Image_Settings_Header'
              : 'GallerySettings_Header'
          )}
          className={isImageSettingsOpened ? classes.header : undefined}
        />

        {!(state.tab === 'design' && state.isImageSettingsOpened) && (
          <TabNavigation
            activeTab={state.tab}
            onTabChange={tab => {
              setState(() => {
                if (tab === 'design') {
                  return {
                    tab,
                    isImageSettingsOpened: false,
                    selectedItemIndexes: [],
                  };
                }
                return { tab };
              });
            }}
          />
        )}

        <Box
          direction="vertical"
          padding="SP3"
          overflowY="auto"
          overflowX="hidden"
          className={classes.content}
        >
          {(() => {
            switch (state.tab) {
              case 'settings': {
                return <GeneralSettings nodeId={nodeId} logBi={logBi} />;
              }
              case 'layout': {
                return <LayoutSettings nodeId={nodeId} logBi={logBi} />;
              }
              case 'design': {
                const selectedImageIndex = galleryItems.findIndex(item => item.selected);
                const selectedImage = galleryItems[selectedImageIndex];

                return state.isImageSettingsOpened ? (
                  <MediaSettingsForm
                    logBi={logBi}
                    closeModal={closeModal}
                    galleryItems={galleryItems}
                    activeImageIndex={selectedImageIndex}
                    accept={accept}
                    itemKey={`${selectedImageIndex}-${selectedImage.image?.media?.src}-${galleryItems.length}`}
                    onFileSelect={onFileSelect}
                    onImageIndexChange={next => {
                      setState(prev => {
                        if (prev.tab === 'design') {
                          return {
                            ...prev,
                            selectedItemIndexes: [next],
                          };
                        }
                        return prev;
                      });
                    }}
                    onDeleteClick={() => {
                      setState(prev => {
                        if (prev.tab === 'design') {
                          const activeImageIndex = prev.selectedItemIndexes[0];
                          if (activeImageIndex === undefined) {
                            throw new Error('No image selected');
                          }

                          const newSelectedIndex = galleryItems[activeImageIndex + 1]
                            ? activeImageIndex
                            : activeImageIndex - 1;

                          const filteredItems = galleryItems.filter(
                            (_item, index) => index !== activeImageIndex
                          );
                          patchPmNodeAttrs({ ...pmNodeAttrs, items: filteredItems });
                          return {
                            ...prev,
                            isImageSettingsOpened: newSelectedIndex !== -1,
                            selectedItemIndexes: newSelectedIndex !== -1 ? [newSelectedIndex] : [],
                          };
                        }
                        return prev;
                      });
                    }}
                    onTitleChange={title => {
                      patchPmNodeAttrs({
                        ...pmNodeAttrs,
                        items: pmNodeAttrs.items?.map((item, index) => {
                          return {
                            ...item,
                            title: index === selectedImageIndex ? title : item.title,
                          };
                        }),
                      });
                    }}
                    onAltTextChange={altText => {
                      patchPmNodeAttrs({
                        ...pmNodeAttrs,
                        items: pmNodeAttrs.items?.map((item, index) => ({
                          ...item,
                          altText: index === selectedImageIndex ? altText : item.altText,
                        })),
                      });
                    }}
                    onLinkSave={value => {
                      patchPmNodeAttrs({
                        ...pmNodeAttrs,
                        items: pmNodeAttrs.items?.map((item, index) => ({
                          ...item,
                          image: item.image?.media
                            ? {
                                ...item.image,
                                link:
                                  index === state.selectedItemIndexes[0]
                                    ? (value as Link)
                                    : item.image?.link,
                              }
                            : undefined,
                        })),
                      });
                    }}
                    onUnlink={() => {
                      patchPmNodeAttrs({
                        ...pmNodeAttrs,
                        items: pmNodeAttrs.items?.map((item, index) => ({
                          ...item,
                          image: item.image?.media
                            ? {
                                ...item.image,
                                link:
                                  index === state.selectedItemIndexes[0]
                                    ? undefined
                                    : item.image?.link,
                              }
                            : undefined,
                        })),
                      });
                    }}
                  />
                ) : (
                  <MediaList
                    logBi={logBi}
                    galleryItems={galleryItems}
                    onChange={({ type, items }) => {
                      if (type === 'delete' || type === 'sort') {
                        patchPmNodeAttrs({
                          ...pmNodeAttrs,
                          items: items.map(item => {
                            const { selected: _, ...rest } = item;
                            return rest;
                          }),
                        });
                      }
                      setState(prev => {
                        if (prev.tab === 'design') {
                          return {
                            ...prev,
                            selectedItemIndexes: items
                              .map((item, index) => (item.selected ? index : undefined))
                              .filter((i): i is number => i !== undefined),
                          };
                        }
                        return prev;
                      });
                    }}
                    onFileSelect={onFileSelect}
                    onEditClick={() => {
                      setState(prev => {
                        if (prev.tab === 'design') {
                          const activeImageIndex = prev.selectedItemIndexes[0];
                          return {
                            ...prev,
                            isImageSettingsOpened: true,
                            selectedItemIndexes: [activeImageIndex],
                          };
                        }
                        return prev;
                      });
                    }}
                    onItemDoubleClick={imageIndex => {
                      setState(prev => {
                        if (prev.tab === 'design') {
                          return {
                            ...prev,
                            selectedItemIndexes: [imageIndex],
                            isImageSettingsOpened: true,
                          };
                        }
                        return prev;
                      });
                    }}
                  />
                );
              }
              default:
                unreachable(state);
                return null;
            }
          })()}
        </Box>
      </Panel>
    </div>
  );
};
